import { Box, useColorModeValue } from '@chakra-ui/react';
import { useStableCallback } from '@main/shared/browser';
import {
  type ReactNode,
  type UIEventHandler,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

export type UseHorizontalScrollShadowOptions = {
  refreshKey?: unknown;
};

// Browsers can report 1-2px of scroll overflow during resize due to subpixel layout.
const SCROLL_OVERFLOW_EPSILON = 2;

function getHorizontalOverflow(scrollWidth: number, clientWidth: number) {
  return scrollWidth - clientWidth;
}

export function useHorizontalScrollShadow<TElement extends HTMLElement>({
  refreshKey,
}: UseHorizontalScrollShadowOptions = {}) {
  const [scrollElement, setScrollElement] = useState<TElement | null>(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);

  const updateShadows = useStableCallback(() => {
    if (!scrollElement) {
      setShowLeftShadow(false);
      setShowRightShadow(false);
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = scrollElement;
    const overflow = getHorizontalOverflow(scrollWidth, clientWidth);

    if (overflow <= SCROLL_OVERFLOW_EPSILON) {
      if (scrollLeft !== 0) {
        scrollElement.scrollLeft = 0;
      }
      setShowLeftShadow(false);
      setShowRightShadow(false);
      return;
    }

    setShowLeftShadow(scrollLeft > SCROLL_OVERFLOW_EPSILON);
    setShowRightShadow(Math.ceil(scrollLeft) + clientWidth < scrollWidth - SCROLL_OVERFLOW_EPSILON);
  });

  const onScroll: UIEventHandler<TElement> = useStableCallback(() => {
    updateShadows();
  });

  useLayoutEffect(() => {
    updateShadows();
  }, [refreshKey, scrollElement, updateShadows]);

  useEffect(() => {
    if (!scrollElement) {
      return;
    }

    updateShadows();
    window.addEventListener('resize', updateShadows);

    if (typeof ResizeObserver === 'undefined') {
      return () => window.removeEventListener('resize', updateShadows);
    }

    const resizeObserver = new ResizeObserver(updateShadows);
    resizeObserver.observe(scrollElement);
    Array.from(scrollElement.children).forEach((child) => resizeObserver.observe(child));

    return () => {
      window.removeEventListener('resize', updateShadows);
      resizeObserver.disconnect();
    };
  }, [scrollElement, updateShadows]);

  const scrollbarThumbColor = useColorModeValue('#E2E8F0', 'rgba(255, 255, 255, 0.16)');
  const leftShadowGradient = useColorModeValue(
    'linear(to-r, rgba(15, 23, 42, 0.10), transparent)',
    'linear(to-r, rgba(0, 0, 0, 0.42), transparent)',
  );
  const rightShadowGradient = useColorModeValue(
    'linear(to-l, rgba(15, 23, 42, 0.10), transparent)',
    'linear(to-l, rgba(0, 0, 0, 0.42), transparent)',
  );

  const maskStyle = useMemo(
    () => ({
      maskImage: 'linear-gradient(to bottom, transparent, black 15%, black 85%, transparent)',
      WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 15%, black 85%, transparent)',
    }),
    [],
  );

  const scrollbarStyles = useMemo(
    () => ({
      '&::-webkit-scrollbar': {
        height: '8px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: scrollbarThumbColor,
        borderRadius: '4px',
      },
    }),
    [scrollbarThumbColor],
  );

  const scrollShadows: ReactNode = useMemo(() => {
    return (
      <>
        {showLeftShadow && (
          <Box
            position="absolute"
            top={0}
            left={0}
            bottom="10px"
            w="24px"
            pointerEvents="none"
            bgGradient={leftShadowGradient}
            style={maskStyle}
          />
        )}
        {showRightShadow && (
          <Box
            position="absolute"
            top={0}
            right={0}
            bottom="10px"
            w="24px"
            pointerEvents="none"
            bgGradient={rightShadowGradient}
            style={maskStyle}
          />
        )}
      </>
    );
  }, [showLeftShadow, showRightShadow, leftShadowGradient, rightShadowGradient, maskStyle]);

  return {
    scrollRef: setScrollElement,
    onScroll,
    scrollbarStyles,
    scrollShadows,
    showLeftShadow,
    showRightShadow,
  };
}
